import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

import { chapterCache } from '@/app/(app)/reader/utils/chapter-cache';
import { formatContent } from '@/app/(app)/reader/utils/format-content';
import {
    DEFAULT_BRIGHTNESS,
    DEFAULT_FONT_SIZE,
    DEFAULT_THEME,
    RENDER_LIST_MAX_CHAPTERS,
    RENDER_LIST_TRIM_KEEP,
} from '@/constants/reader';

import type { IRenderChapter, IReaderState, IUserAsset } from './type';
import type { IBookDetail, ICatalogItem, IReaderBookData } from '@/app/(app)/reader/types';
import type { TReaderTheme } from '@/constants';

// ──────────────────────────────────────────────
// Action 入参类型
// ──────────────────────────────────────────────

/** 恢复由 persistConfig 保存的外观配置，未提供的字段保留当前值。 */
export interface IInitReaderConfigParams {
    theme?: TReaderTheme;
    fontSize?: number;
    brightness?: number;
}

/** 初始来源信息，从 URL 读取后一次性写入，后续扩展字段直接添加到此对象 */
export interface IInitDramaInfo {
    dramaId: string;
    chapterId: string;
    prmtCode: string;
    /** 上一页透传的章节内容地址 */
    articleUrl: string;
    /** 上一页透传的章节标题（对应 v2 接口 title 字段） */
    chapterTitle: string;
    /** 上一页透传的章节序号（对应 v2 接口 sort_no 字段） */
    sortNo: string;
}

// ──────────────────────────────────────────────
// Actions 声明
// ──────────────────────────────────────────────
interface IReaderActions {
    /** 一次性恢复外观配置，仅更新 store，不写回本地缓存。 */
    initReaderConfig: (params: IInitReaderConfigParams) => void;
    /** 切换阅读主题（白天 / 护眼 / 绿色 / 夜间） */
    setTheme: (theme: TReaderTheme) => void;
    /** 设置正文字号（单位 px，取值范围见 FONT_GROUP） */
    setFontSize: (size: number) => void;
    /** 设置亮度（0-100） */
    setBrightness: (brightness: number) => void;
    /** 设置初始化完成标志 */
    setInitReady: (initReady: boolean) => void;
    /** 设置首屏章节详情（v2 接口）就绪标志 */
    setChapterDetailReady: (ready: boolean) => void;
    /** 控制底部设置菜单的显隐 */
    setShowMenu: (show: boolean) => void;
    /** 控制目录侧滑面板的显隐 */
    setShowCatalog: (show: boolean) => void;
    /**
     * 记录初始进入时的来源信息（从 URL 读取，仅调用一次，此后不再变更）。
     */
    initDramaInfo: (payload: IInitDramaInfo) => void;
    /** 目录请求成功后写入目录列表 */
    setCatalogList: (list: ICatalogItem[]) => void;

    // ── 章节渲染区操作 ──────────────────────────────

    // 重置渲染区列表，目录跳转 / 初始加载时触发
    resetRenderList: (data: IReaderBookData) => void;
    /** 向渲染区尾部追加一章（向下预加载） */
    appendChapter: (data: IReaderBookData) => void;
    /**
     * 向渲染区头部插入一章（向上预加载）。
     * 同时写入 unHandlePreId，供 reader-core 在 layoutEffect 中做滚动补偿。
     */
    prependChapter: (data: IReaderBookData) => void;
    /** reader-core 补偿完成后调用，清除 unHandlePreId */
    clearUnHandlePreId: () => void;
    /** reader-core 头部裁切补偿完成后调用，清除 unHandleTrimHeadIds */
    clearUnHandleTrimHeadIds: () => void;
    /** 更新当前锚点章节（由 IntersectionObserver 回调驱动） */
    setAnchorChapterId: (chapterId: string) => void;
    /** 标记章节正在加载（去重用） */
    addLoadingChapter: (chapterId: string) => void;
    /** 标记章节加载结束（成功/失败都要调用） */
    removeLoadingChapter: (chapterId: string) => void;
    /** 设置书籍详情 */
    setBookDetail: (bookDetail: IBookDetail) => void;
    /** 设置用户账号内容 */
    setUserAsset: (userAsset: IUserAsset) => void;
    /** 设置书籍详情 Promise */
    setBookDetailPromise: (bookDetailPromise: Promise<any> | undefined) => void;
    /** 创建 bookDetailReadyPromise（挂载时调用） */
    createBookDetailReadyPromise: () => void;
    /** 完成 bookDetailReadyPromise（bookDetail 获取完成时调用） */
    resolveBookDetailReadyPromise: () => void;
    /** 同步缓存锁状态 */
    syncCacheLockStatus: () => void;
    /** 同步渲染区列表 */
    syncRenderList: () => void;
    /** 更新章节渲染区数据 */
    updateChapterRenderItem: (chapterId: string, newData: IReaderBookData) => void;
    /** 设置购买章节 loading 状态 */
    setPurchaseLoading: (chapterId: string, loading: boolean) => void;
    /** 设置书籍详情 loading 状态 */
    setBookDetailLoading: (loading: boolean) => void;
    /** 重置所有数据 */
    resetAllData: () => void;
    /** 设置购买章节结果 */
    setPurchaseResultMap: (chapterId: string, result: boolean) => void;
    /** 暂存某章节的自动解锁 Toast，等该章节成为阅读锚点后再展示。 */
    setPendingAutoUnlockToast: (chapterId: string, coins: number) => void;
    /** 读取并删除某章节待展示的自动解锁 Toast；无待展示项时返回 null。 */
    consumePendingAutoUnlockToast: (chapterId: string) => number | null;
    /** 设置最近一次目录/菜单跳转的章节 ID */
    setJumpedChapterId: (chapterId: string) => void;
}

// ── bookDetailReadyPromise 的 resolver（存放在 store 中保证并发安全） ───

// 通用的配置缓存函数
const persistConfig = (state: IReaderState) => {
    localStorage.setItem(
        'ReaderConfig',
        JSON.stringify({ theme: state.theme, fontSize: state.fontSize, brightness: state.brightness })
    );
};

// 渲染区回收逻辑，
const trimRenderListInPlace = (list: IRenderChapter[], anchorId: string): IRenderChapter[] => {
    // 当渲染区章节数超过 6 个时，回收多余的章节
    if (list.length <= RENDER_LIST_MAX_CHAPTERS) {
        return list;
    }

    // 如果当前章节不在渲染区，则保留最后 3 章
    const anchorIdx = list.findIndex(c => c.chapterId === anchorId);
    if (anchorIdx === -1) {
        return list.slice(-RENDER_LIST_TRIM_KEEP);
    }

    // 假设当前在第 5 章，总长为 6， 半径为 3，此时为 [1,2,3,4,5,6]
    // 当滚动到第 6 章后，此时为 [1,2,3,4,5,6,7]，然后触发检测逻辑
    // 1. 渲染区的半径长度为 half，half = 3
    const half = Math.floor(RENDER_LIST_MAX_CHAPTERS / 2);
    // 2. 起始位置：锚点往前推 half 章, start = 6 - 3 = 3
    let start = Math.max(0, anchorIdx - half);
    // 3. 结束位置：从开始位置往后推总长，end = Math.min(7, 3+6),因此 end 为 7
    const end = Math.min(list.length, start + RENDER_LIST_MAX_CHAPTERS);
    // 4. 边界补偿：此时从 end 往前推总长，start = Math.max(0, 7-6),因此 start 为 1
    start = Math.max(0, end - RENDER_LIST_MAX_CHAPTERS);
    // 5. 返回截取的章节列表 list.slice(1,7)，即 [2,3,4,5,6,7]
    return list.slice(start, end);
};

const makeRenderChapter = (data: IReaderBookData): IRenderChapter => ({
    chapterId: data.chapterId,
    bookData: data,
    status: 'ready',
});

// ──────────────────────────────────────────────
// 初始 State
// ──────────────────────────────────────────────
const initialState: IReaderState = {
    /** 阅读主题，默认白天模式 */
    theme: DEFAULT_THEME,
    /** 正文字号（px） */
    fontSize: DEFAULT_FONT_SIZE,
    /** 亮度（0-100） */
    brightness: DEFAULT_BRIGHTNESS,
    // url 中的 query 解析完成标志
    queryAnalysisEnd: false,
    /** 章节初始化完成标志 */
    initReady: false,
    /** 首屏章节详情（v2 接口）就绪标志 */
    chapterDetailReady: false,
    /** 底部设置菜单是否可见 */
    showMenu: false,
    /** 目录侧滑面板是否可见 */
    showCatalog: false,
    /** 全书目录列表，由业务层请求后写入 */
    catalogList: [],
    /** 初始进入时从 URL 读取的书籍 ID，初始化后不再变更 */
    dramaId: '',
    /** 初始进入时从 URL 读取的章节 ID，URL 未传时为空字符串，初始化后不再变更 */
    chapterId: '',
    /** URL 中的 promotion_code，用于 v2 章节接口 */
    prmtCode: '',
    /** 上一页透传的章节内容地址，有则跳过 v2 接口直接拉内容 */
    articleUrl: '',
    /** 上一页透传的章节标题（对应 v2 接口 title 字段） */
    chapterTitle: '',
    /** 上一页透传的章节序号（对应 v2 接口 sort_no 字段） */
    sortNo: '',
    /** 章节渲染区，按章节顺序排序 */
    chapterRenderList: [],
    /** 当前锚点章节 ID */
    anchorChapterId: '',
    /** 正在加载中的章节 ID */
    loadingChapterIds: [],
    // 未处理的前插入章节 id，当完成向前的章节插入行为后，需要处理屏幕滚动问题
    // 此字段用于记录需要处理的章节 id，处理完成后，会重新置空
    unHandlePreId: '',
    // 未处理的头部裁切章节 id 列表，当 appendChapter 后 trimRenderListInPlace 裁掉
    // 锚点之前的章节时写入，由 reader-core 在 layoutEffect 中做滚动补偿后清空
    unHandleTrimHeadIds: [],
    // 书籍详情
    bookDetail: {} as IBookDetail,
    // 用户账号内容
    userAsset: {} as IUserAsset,
    // bookDetail 请求 promise
    bookDetailPromise: undefined,
    /** 从阅读器组件挂载到 bookDetail 获取完成的 promise */
    bookDetailReadyPromise: undefined,
    /** bookDetailReadyPromise 的 resolver */
    bookDetailReadyResolver: null,
    // 购买章节 loading map，key 是 chapterId
    purchaseLoadingMap: {},
    // 书籍详情 loading 状态
    bookDetailLoading: false,
    // 购买章节结果 map，key 是 chapterId
    purchaseResultMap: {},
    // 自动解锁成功后，等待用户进入对应章节才展示的 Toast
    pendingAutoUnlockToastMap: {},
    /** 最近一次目录点击跳转的章节 ID */
    jumpedChapterId: '',
};

// ──────────────────────────────────────────────
// Store
// ──────────────────────────────────────────────
export const useReaderStore = create<IReaderState & IReaderActions>()(
    immer((set, get) => ({
        ...initialState,

        initReaderConfig: params => {
            const { theme, fontSize, brightness } = params;
            set(state => {
                if (theme !== undefined) {
                    state.theme = theme;
                }
                if (fontSize !== undefined) {
                    state.fontSize = fontSize;
                }
                if (brightness !== undefined) {
                    state.brightness = brightness;
                }
            });
        },

        setTheme: theme =>
            set(state => {
                state.theme = theme;
                persistConfig(state);
            }),

        setFontSize: fontSize =>
            set(state => {
                state.fontSize = fontSize;
                persistConfig(state);
            }),

        setBrightness: brightness =>
            set(state => {
                state.brightness = brightness;
                persistConfig(state);
            }),

        setInitReady: initReady => set({ initReady }),

        setChapterDetailReady: chapterDetailReady => set({ chapterDetailReady }),

        setShowMenu: showMenu => set({ showMenu }),

        setShowCatalog: showCatalog => set({ showCatalog }),

        initDramaInfo: ({ dramaId, chapterId, prmtCode, articleUrl, chapterTitle, sortNo }) =>
            set(state => {
                state.dramaId = dramaId;
                state.chapterId = chapterId;
                state.prmtCode = prmtCode;
                state.articleUrl = articleUrl;
                state.chapterTitle = chapterTitle;
                state.sortNo = sortNo;
                state.queryAnalysisEnd = true;
            }),

        setCatalogList: list =>
            set(state => {
                state.catalogList = list;
            }),

        resetRenderList: data =>
            set(state => {
                state.chapterRenderList = [makeRenderChapter(data)];
                state.anchorChapterId = data.chapterId;
                state.unHandlePreId = '';
                state.pendingAutoUnlockToastMap = {};
            }),
        updateChapterRenderItem: (chapterId: string, newData: IReaderBookData) =>
            set(state => {
                const index = state.chapterRenderList.findIndex(c => c.chapterId === chapterId);
                if (index !== -1) {
                    state.chapterRenderList[index] = makeRenderChapter(newData);
                }
            }),
        // bookDetail 返回后回填渲染区：同步 title/order/price/isLocked + 推导 prev/nextChapterId
        syncRenderList: () =>
            set(state => {
                const { chapters } = state.bookDetail;
                if (!chapters?.length) {
                    return;
                }

                state.chapterRenderList = state.chapterRenderList.map(c => {
                    const chapterData = chapters.find(ch => ch.chapterId === c.chapterId);
                    if (!chapterData) {
                        return c;
                    }

                    // 按 order 推导前后章
                    const idx = chapters.findIndex(ch => ch.chapterId === c.chapterId);
                    const prevChapter = idx > 0 ? chapters[idx - 1] : undefined;
                    const nextChapter = idx < chapters.length - 1 ? chapters[idx + 1] : undefined;

                    const newTitle = chapterData.title || c.bookData.title;
                    const titleChanged = newTitle && newTitle !== c.bookData.title;
                    return {
                        ...c,
                        bookData: {
                            ...c.bookData,
                            title: newTitle,
                            order: chapterData.order ?? c.bookData.order,
                            price: chapterData.price ?? c.bookData.price,
                            originalPrice: chapterData.originalPrice ?? c.bookData.originalPrice,
                            isLocked: chapterData.isLocked ?? c.bookData.isLocked,
                            isFree: chapterData.isFree ?? c.bookData.isFree,
                            unlockedReason: chapterData.unlockedReason ?? c.bookData.unlockedReason,
                            preChapterId: prevChapter?.chapterId || c.bookData.preChapterId,
                            nextChapterId: nextChapter?.chapterId || c.bookData.nextChapterId,
                            // 标题回填后重新格式化 chapterData（标题嵌入在首段）
                            chapterData: titleChanged
                                ? formatContent(newTitle, c.bookData.content)
                                : c.bookData.chapterData,
                        },
                    };
                });
            }),
        syncCacheLockStatus: () =>
            set(state => {
                state.bookDetail.chapters.forEach(chapter => {
                    const cache = chapterCache.get(chapter.chapterId);
                    if (cache?.isLocked && !chapter.isLocked) {
                        chapterCache.set(chapter.chapterId, {
                            ...cache,
                            isLocked: false,
                        });
                    }
                });
            }),
        appendChapter: data =>
            set(state => {
                if (state.chapterRenderList.some(c => c.chapterId === data.chapterId)) {
                    return;
                }
                const oldList = state.chapterRenderList;
                const anchorIdx = oldList.findIndex(c => c.chapterId === state.anchorChapterId);

                state.chapterRenderList.push(makeRenderChapter(data));
                state.chapterRenderList = trimRenderListInPlace(state.chapterRenderList, state.anchorChapterId);
                // 检测锚点之前的章节是否被裁掉（head-trim），记录 ID 供 reader-core 做滚动补偿
                if (anchorIdx >= 0) {
                    const newIds = new Set(state.chapterRenderList.map(c => c.chapterId));
                    const trimmedHeadIds = oldList
                        .filter((c, idx) => idx < anchorIdx && !newIds.has(c.chapterId))
                        .map(c => c.chapterId);
                    if (trimmedHeadIds.length > 0) {
                        state.unHandleTrimHeadIds = trimmedHeadIds;
                    }
                }
            }),

        prependChapter: data =>
            set(state => {
                if (state.chapterRenderList.some(c => c.chapterId === data.chapterId)) {
                    return;
                }
                state.chapterRenderList.unshift(makeRenderChapter(data));
                state.chapterRenderList = trimRenderListInPlace(state.chapterRenderList, state.anchorChapterId);
                state.unHandlePreId = data.chapterId;
            }),

        clearUnHandlePreId: () =>
            set(state => {
                state.unHandlePreId = '';
            }),

        clearUnHandleTrimHeadIds: () =>
            set(state => {
                state.unHandleTrimHeadIds = [];
            }),

        setAnchorChapterId: chapterId =>
            set(state => {
                if (state.anchorChapterId !== chapterId) {
                    state.anchorChapterId = chapterId;
                }
            }),

        addLoadingChapter: chapterId =>
            set(state => {
                if (!state.loadingChapterIds.includes(chapterId)) {
                    state.loadingChapterIds.push(chapterId);
                }
            }),

        removeLoadingChapter: chapterId =>
            set(state => {
                state.loadingChapterIds = state.loadingChapterIds.filter(id => id !== chapterId);
            }),
        setBookDetail: (bookDetail: IBookDetail) => set({ bookDetail }),
        setUserAsset: (userAsset: IUserAsset) => set({ userAsset }),
        setBookDetailPromise: (promise: Promise<IBookDetail> | undefined) => set({ bookDetailPromise: promise }),
        createBookDetailReadyPromise: () => {
            // 如果 bookDetail 已就绪，直接创建 resolved promise
            if (get().bookDetail?.dramaId) {
                set({ bookDetailReadyPromise: Promise.resolve(), bookDetailReadyResolver: null });
                return;
            }
            const promise = new Promise<void>(resolve => {
                set({ bookDetailReadyResolver: resolve });
            });
            set({ bookDetailReadyPromise: promise });
        },
        resolveBookDetailReadyPromise: () => {
            set(state => {
                if (state.bookDetailReadyResolver) {
                    state.bookDetailReadyResolver();
                    state.bookDetailReadyResolver = null;
                }
            });
        },
        setPurchaseLoading: (chapterId, loading) =>
            set(state => {
                state.purchaseLoadingMap[chapterId] = loading;
            }),
        setBookDetailLoading: (loading: boolean) =>
            set(state => {
                state.bookDetailLoading = loading;
            }),
        resetAllData: () => {
            set(state => {
                if (state.bookDetailReadyResolver) {
                    state.bookDetailReadyResolver();
                }

                return {
                    ...initialState,
                    // 保持阅读器配置不变
                    theme: state.theme,
                    fontSize: state.fontSize,
                    brightness: state.brightness,
                };
            });
        },
        setPurchaseResultMap: (chapterId: string, result: boolean) =>
            set(state => {
                state.purchaseResultMap[chapterId] = result;
            }),
        setPendingAutoUnlockToast: (chapterId: string, coins: number) =>
            set(state => {
                state.pendingAutoUnlockToastMap[chapterId] = coins;
            }),
        consumePendingAutoUnlockToast: (chapterId: string) => {
            let coins: number | null = null;
            set(state => {
                if (Object.prototype.hasOwnProperty.call(state.pendingAutoUnlockToastMap, chapterId)) {
                    coins = state.pendingAutoUnlockToastMap[chapterId];
                    delete state.pendingAutoUnlockToastMap[chapterId];
                }
            });
            return coins;
        },
        setJumpedChapterId: (jumpedChapterId: string) => set({ jumpedChapterId }),
    }))
);
